// src/app/features/claims/store/claims.reducer.ts
//
// IMPORTANT RULE — cache-aware Load handlers:
// Effects return EMPTY when the cache is valid (no API call, no Success action).
// If the reducer sets loading:true on every Load action regardless, it will NEVER
// go back to false when the effect skips the call → infinite loading spinner.
// Solution: each Load handler checks isCacheValid() before changing state.
// If the cache is still valid and forceReload is false → return unchanged state.

import { createReducer, on } from '@ngrx/store';
import {
  ClaimsState,
  initialClaimsState,
  AsyncSlice,
  CacheEntry,
  TTL,
  emptySlice,
  isCacheValid,
} from './claims.state';
import {
  TransakcionetLlojetActions,
  ShendetLlojetActions,
  AksidentLlojetActions,
  AksidentNenLlojetActions,
  BankatActions,
  TrajtuesitActions,
  AdministratoretActions,
  ShtetetActions,
  PerfitimetPerPoliceActions,
  KartelaSearchActions,
  KlinikaSearchActions,
  PerfituesiSearchActions,
  XhirollogariteActions,
  ExchangeRateActions,
  DemitShActions,
  IsiguruariSearchActions,
  PoliceSearchActions,
  KartelaSearchDemActions,
  RapDemitShActions,
  DemiShDetaletActions,
  DemiShVizitatActions,
  DemeShendetTotalActions,
  ValiditetiKartelesActions,
  TransakcionetPerVisitActions,
  ClaimVisitsRestActions,
  FleteTrajtimShendetActions,
  KerkesaDemitShendetActions,
  NjoftimPerLikuidimShActions,
  CvSessionByVisitActions,
  CvAnalysesByVisitActions,
  CvFilesByVisitActions,
  CvSessionsByClaimHealthActions,
  CvLocalAnalysesActions,
  CvMySessionsActions,
  RapDemitShOnlineActions,
  DokTjeraActions,
  KuponaFiskalActions,
  CompanyBankAccountsActions,
  IsiguruaritActions,
  PaymentsSh1Actions,
  PaymentsSh5Actions,
  HistorikPerKarteleActions,
} from './claims.actions';

// ── Helpers ───────────────────────────────────────────────────────────────────

function setLoading<T>(slice: AsyncSlice<T>): AsyncSlice<T> {
  return { ...slice, loading: true, error: null };
}

function setSuccess<T>(slice: AsyncSlice<T>, data: T, ttlMs: number): AsyncSlice<T> {
  const cache: CacheEntry<T> = { data, loadedAt: Date.now(), ttlMs };
  return { data, loading: false, loaded: true, error: null, cache };
}

function setFailure<T>(slice: AsyncSlice<T>, error: string): AsyncSlice<T> {
  return { ...slice, loading: false, error };
}

// ── Reducer ───────────────────────────────────────────────────────────────────

export const claimsReducer = createReducer(
  initialClaimsState,

  // ── TransakcionetLlojet ────────────────────────────────────────────────────
  on(TransakcionetLlojetActions.load, (state, { forceReload }): ClaimsState => {
    if (!forceReload && isCacheValid(state.lookups.transakcioetLlojet.cache)) return state;
    return { ...state, lookups: { ...state.lookups, transakcioetLlojet: setLoading(state.lookups.transakcioetLlojet) } };
  }),
  on(TransakcionetLlojetActions.success, (state, { data }): ClaimsState => ({
    ...state,
    lookups: { ...state.lookups, transakcioetLlojet: setSuccess(state.lookups.transakcioetLlojet, data, TTL.STATIC) },
  })),
  on(TransakcionetLlojetActions.failure, (state, { error }): ClaimsState => ({
    ...state,
    lookups: { ...state.lookups, transakcioetLlojet: setFailure(state.lookups.transakcioetLlojet, error) },
  })),

  // ── ShendetLlojet ──────────────────────────────────────────────────────────
  on(ShendetLlojetActions.load, (state, { forceReload }): ClaimsState => {
    if (!forceReload && isCacheValid(state.lookups.shendetLlojet.cache)) return state;
    return { ...state, lookups: { ...state.lookups, shendetLlojet: setLoading(state.lookups.shendetLlojet) } };
  }),
  on(ShendetLlojetActions.success, (state, { data }): ClaimsState => ({
    ...state,
    lookups: { ...state.lookups, shendetLlojet: setSuccess(state.lookups.shendetLlojet, data, TTL.STATIC) },
  })),
  on(ShendetLlojetActions.failure, (state, { error }): ClaimsState => ({
    ...state,
    lookups: { ...state.lookups, shendetLlojet: setFailure(state.lookups.shendetLlojet, error) },
  })),

  // ── AksidentLlojet ─────────────────────────────────────────────────────────
  on(AksidentLlojetActions.load, (state, { forceReload }): ClaimsState => {
    if (!forceReload && isCacheValid(state.lookups.aksidentLlojet.cache)) return state;
    return { ...state, lookups: { ...state.lookups, aksidentLlojet: setLoading(state.lookups.aksidentLlojet) } };
  }),
  on(AksidentLlojetActions.success, (state, { data }): ClaimsState => ({
    ...state,
    lookups: { ...state.lookups, aksidentLlojet: setSuccess(state.lookups.aksidentLlojet, data, TTL.STATIC) },
  })),
  on(AksidentLlojetActions.failure, (state, { error }): ClaimsState => ({
    ...state,
    lookups: { ...state.lookups, aksidentLlojet: setFailure(state.lookups.aksidentLlojet, error) },
  })),

  // ── AksidentNenLlojet (keyed by btypeId) ───────────────────────────────────
  on(AksidentNenLlojetActions.load, (state, { btypeId, forceReload }): ClaimsState => {
    const current = state.lookups.aksidentNenLlojet[btypeId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      lookups: {
        ...state.lookups,
        aksidentNenLlojet: { ...state.lookups.aksidentNenLlojet, [btypeId]: setLoading(current) },
      },
    };
  }),
  on(AksidentNenLlojetActions.success, (state, { btypeId, data }): ClaimsState => ({
    ...state,
    lookups: {
      ...state.lookups,
      aksidentNenLlojet: {
        ...state.lookups.aksidentNenLlojet,
        [btypeId]: setSuccess(state.lookups.aksidentNenLlojet[btypeId] ?? emptySlice(), data, TTL.STATIC),
      },
    },
  })),
  on(AksidentNenLlojetActions.failure, (state, { btypeId, error }): ClaimsState => ({
    ...state,
    lookups: {
      ...state.lookups,
      aksidentNenLlojet: {
        ...state.lookups.aksidentNenLlojet,
        [btypeId]: setFailure(state.lookups.aksidentNenLlojet[btypeId] ?? emptySlice(), error),
      },
    },
  })),

  // ── Bankat ─────────────────────────────────────────────────────────────────
  on(BankatActions.load, (state, { forceReload }): ClaimsState => {
    if (!forceReload && isCacheValid(state.lookups.bankat.cache)) return state;
    return { ...state, lookups: { ...state.lookups, bankat: setLoading(state.lookups.bankat) } };
  }),
  on(BankatActions.success, (state, { data }): ClaimsState => ({
    ...state,
    lookups: { ...state.lookups, bankat: setSuccess(state.lookups.bankat, data, TTL.STATIC) },
  })),
  on(BankatActions.failure, (state, { error }): ClaimsState => ({
    ...state,
    lookups: { ...state.lookups, bankat: setFailure(state.lookups.bankat, error) },
  })),

  // ── Trajtuesit ─────────────────────────────────────────────────────────────
  on(TrajtuesitActions.load, (state, { forceReload }): ClaimsState => {
    if (!forceReload && isCacheValid(state.lookups.trajtuesit.cache)) return state;
    return { ...state, lookups: { ...state.lookups, trajtuesit: setLoading(state.lookups.trajtuesit) } };
  }),
  on(TrajtuesitActions.success, (state, { data }): ClaimsState => ({
    ...state,
    lookups: { ...state.lookups, trajtuesit: setSuccess(state.lookups.trajtuesit, data, TTL.STATIC) },
  })),
  on(TrajtuesitActions.failure, (state, { error }): ClaimsState => ({
    ...state,
    lookups: { ...state.lookups, trajtuesit: setFailure(state.lookups.trajtuesit, error) },
  })),

  // ── Administratoret ────────────────────────────────────────────────────────
  on(AdministratoretActions.load, (state, { forceReload }): ClaimsState => {
    if (!forceReload && isCacheValid(state.lookups.administratoret.cache)) return state;
    return { ...state, lookups: { ...state.lookups, administratoret: setLoading(state.lookups.administratoret) } };
  }),
  on(AdministratoretActions.success, (state, { data }): ClaimsState => ({
    ...state,
    lookups: { ...state.lookups, administratoret: setSuccess(state.lookups.administratoret, data, TTL.STATIC) },
  })),
  on(AdministratoretActions.failure, (state, { error }): ClaimsState => ({
    ...state,
    lookups: { ...state.lookups, administratoret: setFailure(state.lookups.administratoret, error) },
  })),

  // ── Shtetet (keyed by policaId) ────────────────────────────────────────────
  on(ShtetetActions.load, (state, { policaId, forceReload }): ClaimsState => {
    const current = state.perPolicy.shtetet[policaId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      perPolicy: {
        ...state.perPolicy,
        shtetet: { ...state.perPolicy.shtetet, [policaId]: setLoading(current) },
      },
    };
  }),
  on(ShtetetActions.success, (state, { policaId, data }): ClaimsState => ({
    ...state,
    perPolicy: {
      ...state.perPolicy,
      shtetet: {
        ...state.perPolicy.shtetet,
        [policaId]: setSuccess(state.perPolicy.shtetet[policaId] ?? emptySlice(), data, TTL.PER_POLICY),
      },
    },
  })),
  on(ShtetetActions.failure, (state, { policaId, error }): ClaimsState => ({
    ...state,
    perPolicy: {
      ...state.perPolicy,
      shtetet: {
        ...state.perPolicy.shtetet,
        [policaId]: setFailure(state.perPolicy.shtetet[policaId] ?? emptySlice(), error),
      },
    },
  })),

  // ── PerfitimetPerPolice (keyed by policaId) ────────────────────────────────
  on(PerfitimetPerPoliceActions.load, (state, { policaId, forceReload }): ClaimsState => {
    const current = state.perPolicy.perfitimetPerPolice[policaId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      perPolicy: {
        ...state.perPolicy,
        perfitimetPerPolice: { ...state.perPolicy.perfitimetPerPolice, [policaId]: setLoading(current) },
      },
    };
  }),
  on(PerfitimetPerPoliceActions.success, (state, { policaId, data }): ClaimsState => ({
    ...state,
    perPolicy: {
      ...state.perPolicy,
      perfitimetPerPolice: {
        ...state.perPolicy.perfitimetPerPolice,
        [policaId]: setSuccess(state.perPolicy.perfitimetPerPolice[policaId] ?? emptySlice(), data, TTL.PER_POLICY),
      },
    },
  })),
  on(PerfitimetPerPoliceActions.failure, (state, { policaId, error }): ClaimsState => ({
    ...state,
    perPolicy: {
      ...state.perPolicy,
      perfitimetPerPolice: {
        ...state.perPolicy.perfitimetPerPolice,
        [policaId]: setFailure(state.perPolicy.perfitimetPerPolice[policaId] ?? emptySlice(), error),
      },
    },
  })),

  // ── KartelaSearch ──────────────────────────────────────────────────────────
  // Search results are never cached (always fetch fresh). No cache guard needed.
  on(KartelaSearchActions.search, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, kartelaResults: setLoading(state.searches.kartelaResults) },
  })),
  on(KartelaSearchActions.success, (state, { data }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, kartelaResults: setSuccess(state.searches.kartelaResults, data, TTL.SEARCH) },
  })),
  on(KartelaSearchActions.failure, (state, { error }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, kartelaResults: setFailure(state.searches.kartelaResults, error) },
  })),
  on(KartelaSearchActions.clear, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, kartelaResults: emptySlice() },
  })),

  // ── KlinikaSearch ──────────────────────────────────────────────────────────
  on(KlinikaSearchActions.search, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, klinikaResults: setLoading(state.searches.klinikaResults) },
  })),
  on(KlinikaSearchActions.success, (state, { data }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, klinikaResults: setSuccess(state.searches.klinikaResults, data, TTL.SEARCH) },
  })),
  on(KlinikaSearchActions.failure, (state, { error }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, klinikaResults: setFailure(state.searches.klinikaResults, error) },
  })),
  on(KlinikaSearchActions.clear, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, klinikaResults: emptySlice() },
  })),

  // ── PerfituesiSearch ───────────────────────────────────────────────────────
  on(PerfituesiSearchActions.search, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, perfituesiResults: setLoading(state.searches.perfituesiResults) },
  })),
  on(PerfituesiSearchActions.success, (state, { data }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, perfituesiResults: setSuccess(state.searches.perfituesiResults, data, TTL.SEARCH) },
  })),
  on(PerfituesiSearchActions.failure, (state, { error }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, perfituesiResults: setFailure(state.searches.perfituesiResults, error) },
  })),
  on(PerfituesiSearchActions.clear, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, perfituesiResults: emptySlice() },
  })),

  // ── Xhirollogarite (keyed by personId) ────────────────────────────────────
  on(XhirollogariteActions.load, (state, { personId, forceReload }): ClaimsState => {
    const current = state.searches.xhirollogarite[personId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      searches: {
        ...state.searches,
        xhirollogarite: { ...state.searches.xhirollogarite, [personId]: setLoading(current) },
      },
    };
  }),
  on(XhirollogariteActions.success, (state, { personId, data }): ClaimsState => ({
    ...state,
    searches: {
      ...state.searches,
      xhirollogarite: {
        ...state.searches.xhirollogarite,
        [personId]: setSuccess(state.searches.xhirollogarite[personId] ?? emptySlice(), data, TTL.PER_PERSON),
      },
    },
  })),
  on(XhirollogariteActions.failure, (state, { personId, error }): ClaimsState => ({
    ...state,
    searches: {
      ...state.searches,
      xhirollogarite: {
        ...state.searches.xhirollogarite,
        [personId]: setFailure(state.searches.xhirollogarite[personId] ?? emptySlice(), error),
      },
    },
  })),
  on(XhirollogariteActions.clear, (state, { personId }): ClaimsState => {
    const xhiro = { ...state.searches.xhirollogarite };
    delete xhiro[personId];
    return { ...state, searches: { ...state.searches, xhirollogarite: xhiro } };
  }),

  // ── ExchangeRate ───────────────────────────────────────────────────────────
  on(ExchangeRateActions.success, (state, { currencyCode, rate, date }): ClaimsState => ({
    ...state,
    exchangeRates: { ...state.exchangeRates, [currencyCode]: { rate, date, loadedAt: Date.now() } },
  })),

  // ── DemitSh search ─────────────────────────────────────────────────────────
  on(DemitShActions.search, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, demitShResults: setLoading(state.searches.demitShResults) },
  })),
  on(DemitShActions.success, (state, { data, total }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, demitShResults: setSuccess(state.searches.demitShResults, data, TTL.SEARCH), demitShTotal: total },
  })),
  on(DemitShActions.failure, (state, { error }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, demitShResults: setFailure(state.searches.demitShResults, error) },
  })),
  on(DemitShActions.clear, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, demitShResults: emptySlice() },
  })),

  // ── IsiguruariSearch ───────────────────────────────────────────────────────
  on(IsiguruariSearchActions.search, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, isiguruariResults: setLoading(state.searches.isiguruariResults) },
  })),
  on(IsiguruariSearchActions.success, (state, { data }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, isiguruariResults: setSuccess(state.searches.isiguruariResults, data, TTL.SEARCH) },
  })),
  on(IsiguruariSearchActions.failure, (state, { error }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, isiguruariResults: setFailure(state.searches.isiguruariResults, error) },
  })),
  on(IsiguruariSearchActions.clear, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, isiguruariResults: emptySlice() },
  })),

  // ── PoliceSearch ───────────────────────────────────────────────────────────
  on(PoliceSearchActions.search, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, policeResults: setLoading(state.searches.policeResults) },
  })),
  on(PoliceSearchActions.success, (state, { data }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, policeResults: setSuccess(state.searches.policeResults, data, TTL.SEARCH) },
  })),
  on(PoliceSearchActions.failure, (state, { error }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, policeResults: setFailure(state.searches.policeResults, error) },
  })),
  on(PoliceSearchActions.clear, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, policeResults: emptySlice() },
  })),

  // ── KartelaSearchDem ───────────────────────────────────────────────────────
  on(KartelaSearchDemActions.search, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, kartelaSearchDem: setLoading(state.searches.kartelaSearchDem) },
  })),
  on(KartelaSearchDemActions.success, (state, { data }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, kartelaSearchDem: setSuccess(state.searches.kartelaSearchDem, data, TTL.SEARCH) },
  })),
  on(KartelaSearchDemActions.failure, (state, { error }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, kartelaSearchDem: setFailure(state.searches.kartelaSearchDem, error) },
  })),
  on(KartelaSearchDemActions.clear, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, kartelaSearchDem: emptySlice() },
  })),

  // ── DemiShDetalet (keyed by claimId) ──────────────────────────────────────
  on(DemiShDetaletActions.load, (state, { claimId, forceReload }): ClaimsState => {
    const current = state.searches.claimDetail[claimId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      searches: {
        ...state.searches,
        claimDetail: { ...state.searches.claimDetail, [claimId]: setLoading(current) },
      },
    };
  }),
  on(DemiShDetaletActions.success, (state, { claimId, data }): ClaimsState => ({
    ...state,
    searches: {
      ...state.searches,
      claimDetail: {
        ...state.searches.claimDetail,
        [claimId]: setSuccess(state.searches.claimDetail[claimId] ?? emptySlice(), data, TTL.PER_POLICY),
      },
    },
  })),
  on(DemiShDetaletActions.failure, (state, { claimId, error }): ClaimsState => ({
    ...state,
    searches: {
      ...state.searches,
      claimDetail: {
        ...state.searches.claimDetail,
        [claimId]: setFailure(state.searches.claimDetail[claimId] ?? emptySlice(), error),
      },
    },
  })),

  // ── DemiShVizitat (keyed by claimId) ──────────────────────────────────────
  on(DemiShVizitatActions.load, (state, { claimId, forceReload }): ClaimsState => {
    const current = state.searches.claimVisits[claimId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      searches: {
        ...state.searches,
        claimVisits: { ...state.searches.claimVisits, [claimId]: setLoading(current) },
      },
    };
  }),
  on(DemiShVizitatActions.success, (state, { claimId, data }): ClaimsState => ({
    ...state,
    searches: {
      ...state.searches,
      claimVisits: {
        ...state.searches.claimVisits,
        [claimId]: setSuccess(state.searches.claimVisits[claimId] ?? emptySlice(), data, TTL.PER_POLICY),
      },
    },
  })),
  on(DemiShVizitatActions.failure, (state, { claimId, error }): ClaimsState => ({
    ...state,
    searches: {
      ...state.searches,
      claimVisits: {
        ...state.searches.claimVisits,
        [claimId]: setFailure(state.searches.claimVisits[claimId] ?? emptySlice(), error),
      },
    },
  })),

  // ── DemeShendetTotal (keyed by userId) ────────────────────────────────────
  on(DemeShendetTotalActions.load, (state, { userId, forceReload }): ClaimsState => {
    const current = state.searches.demeShendetTotal[userId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      searches: {
        ...state.searches,
        demeShendetTotal: { ...state.searches.demeShendetTotal, [userId]: setLoading(current) },
      },
    };
  }),
  on(DemeShendetTotalActions.success, (state, { userId, data }): ClaimsState => ({
    ...state,
    searches: {
      ...state.searches,
      demeShendetTotal: {
        ...state.searches.demeShendetTotal,
        [userId]: setSuccess(state.searches.demeShendetTotal[userId] ?? emptySlice(), data, TTL.PER_PERSON),
      },
    },
  })),
  on(DemeShendetTotalActions.failure, (state, { userId, error }): ClaimsState => ({
    ...state,
    searches: {
      ...state.searches,
      demeShendetTotal: {
        ...state.searches.demeShendetTotal,
        [userId]: setFailure(state.searches.demeShendetTotal[userId] ?? emptySlice(), error),
      },
    },
  })),

  // ── ValiditetiKarteles (keyed by "${policaId}_${cardId}") ──────────────────
  on(ValiditetiKartelesActions.load, (state, { policaId, cardId, forceReload }): ClaimsState => {
    const key = `${policaId}_${cardId}`;
    const current = state.searches.validitetiKarteles[key] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      searches: {
        ...state.searches,
        validitetiKarteles: { ...state.searches.validitetiKarteles, [key]: setLoading(current) },
      },
    };
  }),
  on(ValiditetiKartelesActions.success, (state, { policaId, cardId, data }): ClaimsState => {
    const key = `${policaId}_${cardId}`;
    return {
      ...state,
      searches: {
        ...state.searches,
        validitetiKarteles: {
          ...state.searches.validitetiKarteles,
          [key]: setSuccess(state.searches.validitetiKarteles[key] ?? emptySlice(), data, TTL.PER_POLICY),
        },
      },
    };
  }),
  on(ValiditetiKartelesActions.failure, (state, { policaId, cardId, error }): ClaimsState => {
    const key = `${policaId}_${cardId}`;
    return {
      ...state,
      searches: {
        ...state.searches,
        validitetiKarteles: {
          ...state.searches.validitetiKarteles,
          [key]: setFailure(state.searches.validitetiKarteles[key] ?? emptySlice(), error),
        },
      },
    };
  }),

  // ── TransakcionetPerVisit (keyed by visitId) ───────────────────────────────
  on(TransakcionetPerVisitActions.load, (state, { visitId, forceReload }): ClaimsState => {
    const current = state.searches.transakcioetPerVisit[visitId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      searches: {
        ...state.searches,
        transakcioetPerVisit: { ...state.searches.transakcioetPerVisit, [visitId]: setLoading(current) },
      },
    };
  }),
  on(TransakcionetPerVisitActions.success, (state, { visitId, data }): ClaimsState => ({
    ...state,
    searches: {
      ...state.searches,
      transakcioetPerVisit: {
        ...state.searches.transakcioetPerVisit,
        [visitId]: setSuccess(state.searches.transakcioetPerVisit[visitId] ?? emptySlice(), data, TTL.PER_POLICY),
      },
    },
  })),
  on(TransakcionetPerVisitActions.failure, (state, { visitId, error }): ClaimsState => ({
    ...state,
    searches: {
      ...state.searches,
      transakcioetPerVisit: {
        ...state.searches.transakcioetPerVisit,
        [visitId]: setFailure(state.searches.transakcioetPerVisit[visitId] ?? emptySlice(), error),
      },
    },
  })),
  on(TransakcionetPerVisitActions.clear, (state, { visitId }): ClaimsState => {
    const map = { ...state.searches.transakcioetPerVisit };
    delete map[visitId];
    return { ...state, searches: { ...state.searches, transakcioetPerVisit: map } };
  }),

  // ── ClaimVisitsRest (keyed by claimId) ────────────────────────────────────
  on(ClaimVisitsRestActions.load, (state, { claimId, forceReload }): ClaimsState => {
    const current = state.searches.claimVisitsRest[claimId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      searches: {
        ...state.searches,
        claimVisitsRest: { ...state.searches.claimVisitsRest, [claimId]: setLoading(current) },
      },
    };
  }),
  on(ClaimVisitsRestActions.success, (state, { claimId, data }): ClaimsState => ({
    ...state,
    searches: {
      ...state.searches,
      claimVisitsRest: {
        ...state.searches.claimVisitsRest,
        [claimId]: setSuccess(state.searches.claimVisitsRest[claimId] ?? emptySlice(), data, TTL.PER_POLICY),
      },
    },
  })),
  on(ClaimVisitsRestActions.failure, (state, { claimId, error }): ClaimsState => ({
    ...state,
    searches: {
      ...state.searches,
      claimVisitsRest: {
        ...state.searches.claimVisitsRest,
        [claimId]: setFailure(state.searches.claimVisitsRest[claimId] ?? emptySlice(), error),
      },
    },
  })),
  on(ClaimVisitsRestActions.clear, (state, { claimId }): ClaimsState => {
    const map = { ...state.searches.claimVisitsRest };
    delete map[claimId];
    return { ...state, searches: { ...state.searches, claimVisitsRest: map } };
  }),

  // ── FleteTrajtimShendet (keyed by "${claimId}_${cardId}") ─────────────────
  on(FleteTrajtimShendetActions.load, (state, { claimId, cardId, forceReload }): ClaimsState => {
    const key = `${claimId}_${cardId}`;
    const current = state.searches.fleteTrajtimShendet[key] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      searches: {
        ...state.searches,
        fleteTrajtimShendet: { ...state.searches.fleteTrajtimShendet, [key]: setLoading(current) },
      },
    };
  }),
  on(FleteTrajtimShendetActions.success, (state, { claimId, cardId, data }): ClaimsState => {
    const key = `${claimId}_${cardId}`;
    return {
      ...state,
      searches: {
        ...state.searches,
        fleteTrajtimShendet: {
          ...state.searches.fleteTrajtimShendet,
          [key]: setSuccess(state.searches.fleteTrajtimShendet[key] ?? emptySlice(), data, TTL.PER_POLICY),
        },
      },
    };
  }),
  on(FleteTrajtimShendetActions.failure, (state, { claimId, cardId, error }): ClaimsState => {
    const key = `${claimId}_${cardId}`;
    return {
      ...state,
      searches: {
        ...state.searches,
        fleteTrajtimShendet: {
          ...state.searches.fleteTrajtimShendet,
          [key]: setFailure(state.searches.fleteTrajtimShendet[key] ?? emptySlice(), error),
        },
      },
    };
  }),

  // ── KerkesaDemitShendet (keyed by claimId) ─────────────────────────────────
  on(KerkesaDemitShendetActions.load, (state, { claimId, forceReload }): ClaimsState => {
    const current = state.searches.kerkesaDemitShendet[claimId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      searches: {
        ...state.searches,
        kerkesaDemitShendet: { ...state.searches.kerkesaDemitShendet, [claimId]: setLoading(current) },
      },
    };
  }),
  on(KerkesaDemitShendetActions.success, (state, { claimId, data }): ClaimsState => ({
    ...state,
    searches: {
      ...state.searches,
      kerkesaDemitShendet: {
        ...state.searches.kerkesaDemitShendet,
        [claimId]: setSuccess(state.searches.kerkesaDemitShendet[claimId] ?? emptySlice(), data, TTL.PER_POLICY),
      },
    },
  })),
  on(KerkesaDemitShendetActions.failure, (state, { claimId, error }): ClaimsState => ({
    ...state,
    searches: {
      ...state.searches,
      kerkesaDemitShendet: {
        ...state.searches.kerkesaDemitShendet,
        [claimId]: setFailure(state.searches.kerkesaDemitShendet[claimId] ?? emptySlice(), error),
      },
    },
  })),

  // ── NjoftimPerLikuidimSh (keyed by claimId) ────────────────────────────────
  on(NjoftimPerLikuidimShActions.load, (state, { claimId, forceReload }): ClaimsState => {
    const current = state.searches.njoftimPerLikuidimSh[claimId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      searches: {
        ...state.searches,
        njoftimPerLikuidimSh: { ...state.searches.njoftimPerLikuidimSh, [claimId]: setLoading(current) },
      },
    };
  }),
  on(NjoftimPerLikuidimShActions.success, (state, { claimId, data }): ClaimsState => ({
    ...state,
    searches: {
      ...state.searches,
      njoftimPerLikuidimSh: {
        ...state.searches.njoftimPerLikuidimSh,
        [claimId]: setSuccess(state.searches.njoftimPerLikuidimSh[claimId] ?? emptySlice(), data, TTL.PER_POLICY),
      },
    },
  })),
  on(NjoftimPerLikuidimShActions.failure, (state, { claimId, error }): ClaimsState => ({
    ...state,
    searches: {
      ...state.searches,
      njoftimPerLikuidimSh: {
        ...state.searches.njoftimPerLikuidimSh,
        [claimId]: setFailure(state.searches.njoftimPerLikuidimSh[claimId] ?? emptySlice(), error),
      },
    },
  })),

  // ── RapDemitSh ─────────────────────────────────────────────────────────────
  on(RapDemitShActions.search, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, rapDemitShResults: setLoading(state.searches.rapDemitShResults) },
  })),
  on(RapDemitShActions.success, (state, { data, total }): ClaimsState => ({
    ...state,
    searches: {
      ...state.searches,
      rapDemitShResults: setSuccess(state.searches.rapDemitShResults, data, TTL.SEARCH),
      rapDemitShTotal: total,
    },
  })),
  on(RapDemitShActions.failure, (state, { error }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, rapDemitShResults: setFailure(state.searches.rapDemitShResults, error) },
  })),
  on(RapDemitShActions.clear, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, rapDemitShResults: emptySlice(), rapDemitShTotal: 0 },
  })),

  // ── CvSessionByVisit (keyed by visitId — single session) ──────────────────
  on(CvSessionByVisitActions.load, (state, { visitId, forceReload }): ClaimsState => {
    const current = state.searches.cvSessionByVisit[visitId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return { ...state, searches: { ...state.searches, cvSessionByVisit: { ...state.searches.cvSessionByVisit, [visitId]: setLoading(current) } } };
  }),
  on(CvSessionByVisitActions.success, (state, { visitId, data }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, cvSessionByVisit: { ...state.searches.cvSessionByVisit, [visitId]: setSuccess(state.searches.cvSessionByVisit[visitId] ?? emptySlice(), data, TTL.SEARCH) } },
  })),
  on(CvSessionByVisitActions.failure, (state, { visitId, error }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, cvSessionByVisit: { ...state.searches.cvSessionByVisit, [visitId]: setFailure(state.searches.cvSessionByVisit[visitId] ?? emptySlice(), error) } },
  })),

  // ── CvAnalysesByVisit (keyed by visitId) ──────────────────────────────────
  on(CvAnalysesByVisitActions.load, (state, { visitId, forceReload }): ClaimsState => {
    const current = state.searches.cvAnalysesByVisit[visitId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return { ...state, searches: { ...state.searches, cvAnalysesByVisit: { ...state.searches.cvAnalysesByVisit, [visitId]: setLoading(current) } } };
  }),
  on(CvAnalysesByVisitActions.success, (state, { visitId, data }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, cvAnalysesByVisit: { ...state.searches.cvAnalysesByVisit, [visitId]: setSuccess(state.searches.cvAnalysesByVisit[visitId] ?? emptySlice(), data, TTL.SEARCH) } },
  })),
  on(CvAnalysesByVisitActions.failure, (state, { visitId, error }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, cvAnalysesByVisit: { ...state.searches.cvAnalysesByVisit, [visitId]: setFailure(state.searches.cvAnalysesByVisit[visitId] ?? emptySlice(), error) } },
  })),

  // ── CvFilesByVisit (keyed by visitId) ─────────────────────────────────────
  on(CvFilesByVisitActions.load, (state, { visitId, forceReload }): ClaimsState => {
    const current = state.searches.cvFilesByVisit[visitId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return { ...state, searches: { ...state.searches, cvFilesByVisit: { ...state.searches.cvFilesByVisit, [visitId]: setLoading(current) } } };
  }),
  on(CvFilesByVisitActions.success, (state, { visitId, data }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, cvFilesByVisit: { ...state.searches.cvFilesByVisit, [visitId]: setSuccess(state.searches.cvFilesByVisit[visitId] ?? emptySlice(), data, TTL.SEARCH) } },
  })),
  on(CvFilesByVisitActions.failure, (state, { visitId, error }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, cvFilesByVisit: { ...state.searches.cvFilesByVisit, [visitId]: setFailure(state.searches.cvFilesByVisit[visitId] ?? emptySlice(), error) } },
  })),

  // ── CvSessionsByClaimHealth (keyed by claimHealthId) ──────────────────────
  on(CvSessionsByClaimHealthActions.load, (state, { claimHealthId, forceReload }): ClaimsState => {
    const current = state.searches.cvSessionsByClaimHealth[claimHealthId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return { ...state, searches: { ...state.searches, cvSessionsByClaimHealth: { ...state.searches.cvSessionsByClaimHealth, [claimHealthId]: setLoading(current) } } };
  }),
  on(CvSessionsByClaimHealthActions.success, (state, { claimHealthId, data }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, cvSessionsByClaimHealth: { ...state.searches.cvSessionsByClaimHealth, [claimHealthId]: setSuccess(state.searches.cvSessionsByClaimHealth[claimHealthId] ?? emptySlice(), data, TTL.SEARCH) } },
  })),
  on(CvSessionsByClaimHealthActions.failure, (state, { claimHealthId, error }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, cvSessionsByClaimHealth: { ...state.searches.cvSessionsByClaimHealth, [claimHealthId]: setFailure(state.searches.cvSessionsByClaimHealth[claimHealthId] ?? emptySlice(), error) } },
  })),

  // ── CvLocalAnalyses (keyed by cvClaimId) ─────────────────────────────────
  on(CvLocalAnalysesActions.load, (state, { cvClaimId, forceReload }): ClaimsState => {
    const current = state.searches.cvLocalAnalyses[cvClaimId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return { ...state, searches: { ...state.searches, cvLocalAnalyses: { ...state.searches.cvLocalAnalyses, [cvClaimId]: setLoading(current) } } };
  }),
  on(CvLocalAnalysesActions.success, (state, { cvClaimId, data }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, cvLocalAnalyses: { ...state.searches.cvLocalAnalyses, [cvClaimId]: setSuccess(state.searches.cvLocalAnalyses[cvClaimId] ?? emptySlice(), data, TTL.SEARCH) } },
  })),
  on(CvLocalAnalysesActions.failure, (state, { cvClaimId, error }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, cvLocalAnalyses: { ...state.searches.cvLocalAnalyses, [cvClaimId]: setFailure(state.searches.cvLocalAnalyses[cvClaimId] ?? emptySlice(), error) } },
  })),

  // ── CvMySessions (no key — current user) ─────────────────────────────────
  on(CvMySessionsActions.load, (state, { forceReload }): ClaimsState => {
    if (!forceReload && isCacheValid(state.searches.cvMySessions.cache)) return state;
    return { ...state, searches: { ...state.searches, cvMySessions: setLoading(state.searches.cvMySessions) } };
  }),
  on(CvMySessionsActions.success, (state, { data }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, cvMySessions: setSuccess(state.searches.cvMySessions, data, TTL.SEARCH) },
  })),
  on(CvMySessionsActions.failure, (state, { error }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, cvMySessions: setFailure(state.searches.cvMySessions, error) },
  })),

  // ── RapDemitShOnline (no cache guard — switchMap handles cancellation) ────
  on(RapDemitShOnlineActions.search, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, rapDemitShOnlineResults: setLoading(state.searches.rapDemitShOnlineResults) },
  })),
  on(RapDemitShOnlineActions.success, (state, { data }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, rapDemitShOnlineResults: setSuccess(state.searches.rapDemitShOnlineResults, data, TTL.SEARCH) },
  })),
  on(RapDemitShOnlineActions.failure, (state, { error }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, rapDemitShOnlineResults: setFailure(state.searches.rapDemitShOnlineResults, error) },
  })),
  on(RapDemitShOnlineActions.clear, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, rapDemitShOnlineResults: emptySlice() },
  })),

  // ── DokTjera (keyed by raportimiId) ──────────────────────────────────────
  on(DokTjeraActions.load, (state, { raportimiId, forceReload }): ClaimsState => {
    const current = state.searches.dokTjera[raportimiId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return { ...state, searches: { ...state.searches, dokTjera: { ...state.searches.dokTjera, [raportimiId]: setLoading(current) } } };
  }),
  on(DokTjeraActions.success, (state, { raportimiId, data }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, dokTjera: { ...state.searches.dokTjera, [raportimiId]: setSuccess(state.searches.dokTjera[raportimiId] ?? emptySlice(), data, TTL.PER_POLICY) } },
  })),
  on(DokTjeraActions.failure, (state, { raportimiId, error }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, dokTjera: { ...state.searches.dokTjera, [raportimiId]: setFailure(state.searches.dokTjera[raportimiId] ?? emptySlice(), error) } },
  })),

  // ── KuponaFiskal (keyed by raportimiId) ──────────────────────────────────
  on(KuponaFiskalActions.load, (state, { raportimiId, forceReload }): ClaimsState => {
    const current = state.searches.kuponaFiskal[raportimiId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return { ...state, searches: { ...state.searches, kuponaFiskal: { ...state.searches.kuponaFiskal, [raportimiId]: setLoading(current) } } };
  }),
  on(KuponaFiskalActions.success, (state, { raportimiId, data }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, kuponaFiskal: { ...state.searches.kuponaFiskal, [raportimiId]: setSuccess(state.searches.kuponaFiskal[raportimiId] ?? emptySlice(), data, TTL.PER_POLICY) } },
  })),
  on(KuponaFiskalActions.failure, (state, { raportimiId, error }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, kuponaFiskal: { ...state.searches.kuponaFiskal, [raportimiId]: setFailure(state.searches.kuponaFiskal[raportimiId] ?? emptySlice(), error) } },
  })),

  // ── PAYMENT PROCESSING (rapdemepagese_sh) ────────────────────────────────

  // CompanyBankAccounts (static lookup)
  on(CompanyBankAccountsActions.load, (state, { forceReload }): ClaimsState => {
    if (!forceReload && isCacheValid(state.lookups.companyBankAccounts.cache)) return state;
    return { ...state, lookups: { ...state.lookups, companyBankAccounts: setLoading(state.lookups.companyBankAccounts) } };
  }),
  on(CompanyBankAccountsActions.success, (state, { data }): ClaimsState => ({
    ...state,
    lookups: { ...state.lookups, companyBankAccounts: setSuccess(state.lookups.companyBankAccounts, data, TTL.STATIC) },
  })),
  on(CompanyBankAccountsActions.failure, (state, { error }): ClaimsState => ({
    ...state,
    lookups: { ...state.lookups, companyBankAccounts: setFailure(state.lookups.companyBankAccounts, error) },
  })),

  // Isiguruarit (keyed by lloji 1|2|3)
  on(IsiguruaritActions.load, (state, { lloji, forceReload }): ClaimsState => {
    const current = state.lookups.isiguruarit[lloji] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      lookups: {
        ...state.lookups,
        isiguruarit: { ...state.lookups.isiguruarit, [lloji]: setLoading(current) },
      },
    };
  }),
  on(IsiguruaritActions.success, (state, { lloji, data }): ClaimsState => ({
    ...state,
    lookups: {
      ...state.lookups,
      isiguruarit: {
        ...state.lookups.isiguruarit,
        [lloji]: setSuccess(state.lookups.isiguruarit[lloji] ?? emptySlice(), data, TTL.STATIC),
      },
    },
  })),
  on(IsiguruaritActions.failure, (state, { lloji, error }): ClaimsState => ({
    ...state,
    lookups: {
      ...state.lookups,
      isiguruarit: {
        ...state.lookups.isiguruarit,
        [lloji]: setFailure(state.lookups.isiguruarit[lloji] ?? emptySlice(), error),
      },
    },
  })),

  // PaymentsSh1 (no cache guard — switchMap handles cancellation)
  on(PaymentsSh1Actions.load, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, paymentsSh1: setLoading(state.searches.paymentsSh1) },
  })),
  on(PaymentsSh1Actions.success, (state, { data }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, paymentsSh1: setSuccess(state.searches.paymentsSh1, data, TTL.SEARCH) },
  })),
  on(PaymentsSh1Actions.failure, (state, { error }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, paymentsSh1: setFailure(state.searches.paymentsSh1, error) },
  })),
  on(PaymentsSh1Actions.clear, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, paymentsSh1: emptySlice() },
  })),

  // PaymentsSh5 (no cache guard — switchMap handles cancellation)
  on(PaymentsSh5Actions.load, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, paymentsSh5: setLoading(state.searches.paymentsSh5) },
  })),
  on(PaymentsSh5Actions.success, (state, { data }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, paymentsSh5: setSuccess(state.searches.paymentsSh5, data, TTL.SEARCH) },
  })),
  on(PaymentsSh5Actions.failure, (state, { error }): ClaimsState => ({
    ...state,
    searches: { ...state.searches, paymentsSh5: setFailure(state.searches.paymentsSh5, error) },
  })),
  on(PaymentsSh5Actions.clear, (state): ClaimsState => ({
    ...state,
    searches: { ...state.searches, paymentsSh5: emptySlice() },
  })),

  // ── HistorikPerKartele (keyed by claimId) ──────────────────────────────────
  on(HistorikPerKarteleActions.load, (state, { claimId, forceReload }): ClaimsState => {
    const current = state.searches.historikPerKartele[claimId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      searches: {
        ...state.searches,
        historikPerKartele: { ...state.searches.historikPerKartele, [claimId]: setLoading(current) },
      },
    };
  }),
  on(HistorikPerKarteleActions.success, (state, { claimId, data }): ClaimsState => ({
    ...state,
    searches: {
      ...state.searches,
      historikPerKartele: {
        ...state.searches.historikPerKartele,
        [claimId]: setSuccess(state.searches.historikPerKartele[claimId] ?? emptySlice(), data, TTL.PER_POLICY),
      },
    },
  })),
  on(HistorikPerKarteleActions.failure, (state, { claimId, error }): ClaimsState => ({
    ...state,
    searches: {
      ...state.searches,
      historikPerKartele: {
        ...state.searches.historikPerKartele,
        [claimId]: setFailure(state.searches.historikPerKartele[claimId] ?? emptySlice(), error),
      },
    },
  })),
  on(HistorikPerKarteleActions.clear, (state, { claimId }): ClaimsState => {
    const map = { ...state.searches.historikPerKartele };
    delete map[claimId];
    return { ...state, searches: { ...state.searches, historikPerKartele: map } };
  }),
);
